<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n()

useHead({
  titleTemplate: () => `${t('n8nAiAgents.title')} | SMSBAT`,
  meta: [
    { name: "description", content: () => t('n8nAiAgents.subtitle') },
    { property: "og:title", content: () => `${t('n8nAiAgents.title')} | SMSBAT` },
    { property: "og:description", content: () => t('n8nAiAgents.subtitle') },
    { property: "og:image", content: "https://smsbat.com/images/chatbot.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})
</script>

<template>
  <div class="n8n-ai-page">
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-text">
            <h1 class="banner-title">
              {{ $t('n8nAiAgents.title') }}
            </h1>
            <p class="banner-description">
              {{ $t('n8nAiAgents.subtitle') }}
            </p>
            <div class="banner-action">
              <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
                <p>{{ $t('n8nAiAgents.hero.getStarted') }}</p>
              </NuxtLink>
              <button class="button outlined" disabled>
                <p>{{ $t('n8nAiAgents.hero.viewExamples') }}</p>
              </button>
            </div>
          </div>
          <div class="banner-image-container">
          <NuxtImg preload sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" src="/images/chatbot.png" class="mt-20 w-full h-auto object-contain" alt="n8n AI Agents" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('n8nAiAgents.stats.title') }}</div>
          <div class="section-head-description">{{ $t('n8nAiAgents.stats.subtitle') }}</div>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-number">6+</div>
            <div class="stat-label">{{ $t('n8nAiAgents.stats.platforms') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">24/7</div>
            <div class="stat-label">{{ $t('n8nAiAgents.stats.support') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">99.9%</div>
            <div class="stat-label">{{ $t('n8nAiAgents.stats.uptime') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">100+</div>
            <div class="stat-label">{{ $t('n8nAiAgents.stats.workflows') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('n8nAiAgents.features.title') }}</div>
          <div class="section-head-description">{{ $t('n8nAiAgents.features.subtitle') }}</div>
        </div>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6"></circle>
                <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('n8nAiAgents.features.intelligent.title') }}</h3>
            <p>{{ $t('n8nAiAgents.features.intelligent.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6"></circle>
                <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('n8nAiAgents.features.multiPlatform.title') }}</h3>
            <p>{{ $t('n8nAiAgents.features.multiPlatform.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6"></circle>
                <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('n8nAiAgents.features.workflow.title') }}</h3>
            <p>{{ $t('n8nAiAgents.features.workflow.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('n8nAiAgents.pricing.title') }}</div>
          <div class="section-head-description">{{ $t('n8nAiAgents.pricing.subtitle') }}</div>
        </div>
        <div class="pricing-content">
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('n8nAiAgents.pricing.starter.title') }}</h3>
              <div class="price">{{ $t('n8nAiAgents.pricing.starter.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.starter.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.starter.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.starter.feature3') }}</div>
              </div>
            </div>
          </div>
          <div class="pricing-card featured">
            <div class="pricing-header">
              <h3>{{ $t('n8nAiAgents.pricing.professional.title') }}</h3>
              <div class="price">{{ $t('n8nAiAgents.pricing.professional.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.professional.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.professional.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.professional.feature3') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.professional.feature4') }}</div>
              </div>
            </div>
          </div>
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('n8nAiAgents.pricing.enterprise.title') }}</h3>
              <div class="price">{{ $t('n8nAiAgents.pricing.enterprise.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.enterprise.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.enterprise.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.enterprise.feature3') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('n8nAiAgents.pricing.enterprise.feature4') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('n8nAiAgents.cta.title') }}</div>
          <div class="section-head-description">{{ $t('n8nAiAgents.cta.description') }}</div>
        </div>
        <div class="banner-action cta-actions">
          <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
            <p>{{ $t('n8nAiAgents.cta.getStarted') }}</p>
          </NuxtLink>
          <button class="button outlined" disabled>
            <p>{{ $t('n8nAiAgents.cta.viewExamples') }}</p>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.n8n-ai-page {
  padding-top: 100px;
}

.main-banner {
  padding: 80px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: hidden;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.banner-description {
  font-size: 1.25rem;
  margin-bottom: 30px;
  opacity: 0.9;
  line-height: 1.6;
}

.banner-action {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 30px;
}

.cta-actions {
  justify-content: center;
}

.main-banner .banner-action .button {
  width: 225px;
}

.banner-image-container {
  text-align: center;
}

.banner-image-container img {
  max-width: 100%;
  height: auto;
}

.page-section {
  padding: 80px 0;
}

.section-head {
  text-align: center;
  margin-bottom: 60px;
}

.section-head-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #1f2937;
}

.section-head-description {
  font-size: 1.25rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.stat-item {
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 10px;
  }
}

.stat-number {
  font-size: 3rem;
  font-weight: 700;
  color: #3070b6;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
}

.stat-label {
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 600;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.feature-item {
  text-align: center;
  padding: 40px 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-icon {
  margin-bottom: 20px;
}

.feature-item h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #1f2937;
}

.feature-item p {
  color: #6b7280;
  line-height: 1.6;
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;
}

.bottom-banner .section-head-title,
.bottom-banner .section-head-description {
  color: white;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 52px;
  font-size: 18px;
  font-weight: 800;
  border: 2px solid #3070b6;
  border-radius: 10px;
  transition: 0.2s ease;
  text-decoration: none;
  cursor: pointer;
}

.button.filled {
  color: #fff;
  background: #3070b6;
}

.button.filled:hover {
  color: #3070b6;
  background: #fff;
}

.button.outlined {
  color: #3070b6;
  background: #fff;
}

.button.outlined:hover {
  color: #fff;
  background: #3070b6;
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button:disabled:hover {
  color: #3070b6;
  background: #fff;
}

@media (max-width: 768px) {
  .banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 0 15px;
  }

  .banner-title {
    font-size: 2rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .features-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .banner-content {
    padding: 0 10px;
  }

  .banner-title {
    font-size: 1.6rem;
  }

  .banner-description {
    font-size: 1rem;
  }
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.pricing-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;
}

.pricing-card {
  padding: 40px 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  position: relative;
}

.pricing-card:hover {
  transform: translateY(-5px);
}

.pricing-card.featured {
  border: 2px solid #3070b6;
  transform: scale(1.05);
}

.pricing-card.featured:hover {
  transform: scale(1.05) translateY(-5px);
}

.pricing-header {
  text-align: center;
  margin-bottom: 30px;
}

.pricing-header h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.price {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3070b6;
}

.pricing-features .feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 0;
  background: none;
  box-shadow: none;
  text-align: left;
}

.pricing-features .feature-item:hover {
  transform: none;
}

.pricing-features .feature-icon {
  width: 20px;
  height: 20px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.pricing-features .feature-text {
  font-size: 1rem;
  color: #333;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .main-banner {
    .banner-title {
      margin: 0 auto;
      text-align: center;
    }

    .banner-description {
      margin: 0 auto;
      text-align: center;
    }
  }
}

@media (max-width: 992px) {
  .main-banner {
    padding: 0 0 80px 0;
  }

  .n8n-ai-page {
    padding-top: 0;
  }
}
</style>